// Net movement (92 A7): own-player prediction + reconciliation, remote replay smoothing.
// Pure: works on an engine PathMover in timed mode, no SDK or DOM.
import { commitPoint, GAIT_MULT, isValidPackedPath, MAX_PATH_TILES, packPathFromTiles, STEP_MS_WALK } from '@enugu/shared';

/** Remote players replay on serverNow - D (fixed D, A7). */
export const REMOTE_DELAY_MS = 200;
/** Own-player correction: lerp over 150 ms, snap beyond 2 tiles. */
export const BLEND_MS = 150;
export const SNAP_TILES = 2;
/** Remote catch-up: at most 1.5x walk speed, snap beyond 3 tiles (40 §3.10). */
export const CATCHUP_MULT = 1.5;
export const REMOTE_SNAP_TILES = 3;

/** uint16 sequence order with wrap: true if a is older than b. */
export function seqOlder(a, b) {
    const d = (b - a) & 0xffff;
    return d !== 0 && d < 0x8000;
}

export const sameRoute = (a, b) => a.x === b.x && a.y === b.y && a.path === b.path && (a.gait ?? 0) === (b.gait ?? 0);

/** Copies the wire mover fields we use (Schema instance or plain object). */
export const readMover = (m) => ({ x: m.x, y: m.y, path: m.path ?? '', startAt: m.startAt, gait: m.gait ?? 0, seq: m.seq ?? 0 });

/** Display smoothing for a remote PathMover: catch up at <= 1.5x speed, snap if > 3 tiles behind. */
export function remoteSmoother() {
    let dx = NaN;
    let dy = NaN;
    return (m, dt) => {
        const tx = m.x;
        const ty = m.y;
        const ex = tx - dx;
        const ey = ty - dy;
        const dist = Math.hypot(ex, ey);
        if (!(dist <= REMOTE_SNAP_TILES)) {
            dx = tx;
            dy = ty;
            m.prevX = tx;
            m.prevY = ty;
            return;
        }
        const g = m.timed?.gait ?? 0;
        const max = (CATCHUP_MULT * 1000 * dt) / (STEP_MS_WALK * (GAIT_MULT[g] ?? 1));
        if (dist > max) {
            dx += (ex / dist) * max;
            dy += (ey / dist) * max;
        } else {
            dx = tx;
            dy = ty;
        }
        m.x = dx;
        m.y = dy;
    };
}

/**
 * Own player: predicts with the server's rules (commitPoint, positionAt) on
 * the synced clock, then reconciles against the server's mover.
 */
export class OwnPredictor {
    constructor(mover, serverNow, localNow = () => performance.now()) {
        this.mover = mover;
        this.serverNow = serverNow;
        this.localNow = localNow;
        this.seq = 0;
        this.joined = false;
        this.blend = null;
        this.mover.smooth = (m) => this.applyBlend(m);
    }
    /** Plans a move to `goal` from the commit point. Returns the Move/Stop payload or null. */
    moveTo(goal, astar) {
        const t = this.serverNow();
        const base = this.mover.timedAt(t) ?? { x: this.mover.tileX, y: this.mover.tileY, path: '', startAt: t, gait: 0 };
        const c = commitPoint(base, t);
        let tiles = astar.findPath(c.x, c.y, goal.x, goal.y, { allowPartial: true, maxExpansions: 4000 });
        if (!tiles) return null;
        if (tiles.length > MAX_PATH_TILES) tiles = tiles.slice(0, MAX_PATH_TILES);
        let path = packPathFromTiles(c.x, c.y, tiles);
        while (!isValidPackedPath(path)) {
            tiles = tiles.slice(0, -1);
            path = packPathFromTiles(c.x, c.y, tiles);
        }
        this.seq = (this.seq + 1) & 0xffff;
        this.mover.setTimed({ x: c.x, y: c.y, path, startAt: c.at, gait: 0 }, this.serverNow, t);
        if (tiles.length === 0) return { type: 'stop', msg: { seq: this.seq } };
        const end = tiles[tiles.length - 1];
        return { type: 'move', msg: { seq: this.seq, to: [end.x, end.y], from: [c.x, c.y], path } };
    }
    /** Server patch for my mover. Returns 'snap' | 'pending' | 'ok' | 'blend'. */
    onServer(sm) {
        if (!this.joined) {
            this.joined = true;
            this.seq = sm.seq ?? 0;
            this.adopt(sm, true);
            return 'snap';
        }
        if (sm.seq !== undefined && seqOlder(sm.seq, this.seq)) return 'pending';
        if (this.mover.timed && sameRoute(sm, this.mover.timed)) return 'ok';
        return this.adopt(sm, false);
    }
    /** MoveRejected {seq, at: [x, y]}: walk back to the authoritative tile. */
    onRejected(msg) {
        if (msg.seq !== this.seq || !Array.isArray(msg.at)) return 'ignored';
        return this.adopt({ x: msg.at[0], y: msg.at[1], path: '', startAt: this.serverNow(), gait: 0 }, false);
    }
    adopt(sm, forceSnap) {
        const m = this.mover;
        const bx = m.x;
        const by = m.y;
        m.resetTimed(sm, this.serverNow);
        m.replayAt(this.serverNow());
        const ox = bx - m.x;
        const oy = by - m.y;
        if (forceSnap || Math.hypot(ox, oy) > SNAP_TILES) {
            this.blend = null;
            m.prevX = m.x;
            m.prevY = m.y;
            return 'snap';
        }
        this.blend = { ox, oy, t0: this.localNow() };
        m.x += ox;
        m.y += oy;
        return 'blend';
    }
    applyBlend(m) {
        const b = this.blend;
        if (!b) return;
        const k = 1 - (this.localNow() - b.t0) / BLEND_MS;
        if (k <= 0) {
            this.blend = null;
            return;
        }
        m.x += b.ox * k;
        m.y += b.oy * k;
    }
}
